import { Theme, useTheme } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import type {
  Annotations,
  Data,
  Layout as PlotlyLayout,
  Shape,
} from "plotly.js";
import Plot from "components/plotly/LocalisedPlot";
import {
  ImpuritiesConstraint,
  ImpuritiesPlotProps,
  generateBasePlotLayoutStructure,
  getXAxisId,
  getYAxisId,
} from "./ImpuritiesPlotCommon";
import { Serialiser } from "hooks/serialisers/types";
import { NumberSerialiserOptions } from "hooks/serialisers/types";
import { useNumberSerialiser } from "hooks/serialisers/numbers";

const mean = (constraint: ImpuritiesConstraint) => {
  // if we don't have mean, show median (probably very close).
  return constraint.mean ?? constraint.quantiles[0.5] ?? 0;
};

const generateBarPlotData = (
  constraints: ImpuritiesConstraint[],
  theme: Theme
): Partial<Data>[] => {
  const data = constraints.map((constraint, i) => {
    const xAxisId = getXAxisId(i);
    const yAxisId = getYAxisId(i);

    const barData: Partial<Data> = {
      type: "bar",
      x: [""],
      y: [mean(constraint)],
      name: constraint.name,
      marker: {
        color: Object.values(theme.palette.data)[
          i % Object.values(theme.palette.data).length
        ],
      },
      xaxis: xAxisId,
      yaxis: yAxisId,
      width: 0.4,
    };
    return barData;
  });
  return data;
};

interface GenerateBarPlotLayoutProps {
  constraints: ImpuritiesConstraint[];
  theme: Theme;
  height?: number;
  t: (key: string) => string;
  serialiser: Serialiser<number, NumberSerialiserOptions<never>>;
}

const generateBarPlotLayout = ({
  constraints,
  theme,
  height,
  t,
  serialiser,
}: GenerateBarPlotLayoutProps): Partial<PlotlyLayout> => {
  const layout = generateBasePlotLayoutStructure({
    constraints,
    theme,
    getMaxValueRendered: (constraint) => mean(constraint),
    height,
  });

  const barAnnotations = constraints.flatMap(
    (constraint, i): Partial<Annotations>[] => {
      const xAxisId = getXAxisId(i);
      const yAxisId = getYAxisId(i);
      const fractionOfMaxTolerance =
        mean(constraint) / Math.max(constraint.weightPercentMaxTolerance, 1e-6);

      return [
        {
          text: `↑ ${serialiser.format(fractionOfMaxTolerance * 100, {
            decimalPlaces: 2,
          })}%`,
          x: 0.4,
          y: constraint.weightPercentMaxTolerance,
          xref: xAxisId,
          yref: yAxisId,
          showarrow: false,
          yanchor: "bottom",
          xanchor: "right",
          font: { size: 12 },
        },
        {
          text: t("ofMaximum"),
          x: 0.4,
          y: constraint.weightPercentMaxTolerance,
          xref: xAxisId,
          yref: yAxisId,
          showarrow: false,
          yanchor: "top",
          xanchor: "right",
          font: { size: 12 },
        },
      ];
    }
  );

  layout.annotations = [...(layout.annotations ?? []), ...barAnnotations];

  const barShapes: Partial<Shape>[] = constraints.flatMap((constraint, i) => {
    const xAxisId = getXAxisId(i);
    const yAxisId = getYAxisId(i);
    return [
      {
        type: "line" as const,
        x0: -0.2,
        x1: 0.2,
        y0: constraint.weightPercentMaxTolerance,
        y1: constraint.weightPercentMaxTolerance,
        xref: xAxisId,
        yref: yAxisId,
        line: { color: theme.palette.text.primary, width: 2 },
      },
    ];
  });
  layout.shapes = barShapes;

  return layout;
};

export const ImpuritiesBarPlot = ({
  constraints,
  height,
}: ImpuritiesPlotProps) => {
  const theme = useTheme();
  const { t } = useTenantTranslation();
  const serialiser = useNumberSerialiser({
    decimalPlaces: 3,
  });

  const data = generateBarPlotData(constraints, theme);
  const layoutPlotly: Partial<PlotlyLayout> = generateBarPlotLayout({
    constraints,
    height,
    theme,
    serialiser,
    t,
  });

  return (
    <Plot
      style={{ width: "100%" }}
      data={data}
      layout={layoutPlotly}
      config={{ responsive: false, displaylogo: false }}
    />
  );
};
